/**
 * Number formatting for the Executive view — shared by the on-screen tiles
 * and the downloaded slide, so both show the same figures the same way.
 */
import type { ExecutiveSummary, Tool } from '@ace-tracker/shared';

export const TOOL_NAMES: Record<Tool, string> = {
  copilot: 'GitHub Copilot',
  'claude-code': 'Claude Code',
  codex: 'OpenAI Codex',
  kiro: 'AWS Kiro',
  gemini: 'Gemini CLI',
  cursor: 'Cursor',
  windsurf: 'Windsurf',
};

/** Tools with developers or spend in the period, so an unused tool takes no row; all of them when none was used. */
export function toolsInUse(summary: ExecutiveSummary): ExecutiveSummary['tools'] {
  const used = summary.tools.filter((t) => t.activeDevelopers > 0 || (t.spend ?? 0) > 0);
  return used.length > 0 ? used : summary.tools;
}

export type DeltaDirection = 'up' | 'down' | 'flat';
/** good = up is good (adoption), neutral = direction carries no judgement (spend). */
export type DeltaTone = 'good' | 'bad' | 'neutral';

export interface Delta {
  direction: DeltaDirection;
  text: string;
  tone: DeltaTone;
}

function tone(direction: DeltaDirection, upIsGood: boolean): DeltaTone {
  if (!upIsGood || direction === 'flat') return 'neutral';
  return direction === 'up' ? 'good' : 'bad';
}

/** Relative change, e.g. "17%". */
export function percentChange(current: number, previous: number, upIsGood = true): Delta | null {
  if (previous <= 0) return current > 0 ? { direction: 'up', text: 'New', tone: tone('up', upIsGood) } : null;
  const change = Math.round(((current - previous) / previous) * 100);
  const direction: DeltaDirection = change > 0 ? 'up' : change < 0 ? 'down' : 'flat';
  return { direction, text: `${Math.abs(change)}%`, tone: tone(direction, upIsGood) };
}

/** Change in percentage points, e.g. "10 pts". */
export function pointsChange(current: number | null, previous: number | null): Delta | null {
  if (current === null || previous === null) return null;
  const change = Math.round(current - previous);
  const direction: DeltaDirection = change > 0 ? 'up' : change < 0 ? 'down' : 'flat';
  return { direction, text: `${Math.abs(change)} ${Math.abs(change) === 1 ? 'pt' : 'pts'}`, tone: tone(direction, true) };
}

/** "$4,480", or an em dash for a tool whose spend is not included. */
export function formatToolMoney(value: number | null, currency: string): string {
  return value === null ? '—' : formatMoney(value, currency);
}

/** "GitHub Copilot billing not included", or '' when every tool in use is in the spend total. */
export function spendExclusionNote(summary: ExecutiveSummary): string {
  const shown = new Set(toolsInUse(summary).map((t) => t.tool));
  const names = summary.spend.excludedTools.filter((t) => shown.has(t)).map((t) => TOOL_NAMES[t]);
  if (names.length === 0) return '';
  const list = names.length <= 2 ? names.join(' and ') : `${names.slice(0, -1).join(', ')}, and ${names[names.length - 1]}`;
  return `${list} billing not included`;
}

export function formatPercent(value: number | null): string {
  return value === null ? '—' : `${Math.round(value)}%`;
}

export function formatCount(value: number): string {
  return Math.round(value).toLocaleString('en-US');
}

/** "$14.3K" for headline figures. */
export function formatCompactMoney(value: number, currency: string): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    notation: 'compact',
    maximumFractionDigits: value >= 1000 ? 1 : 0,
  }).format(value);
}

/** "$4,480" for table cells. */
export function formatMoney(value: number, currency: string): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency, maximumFractionDigits: 0 }).format(value);
}

function formatDay(iso: string, withYear: boolean): string {
  return new Date(iso).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    ...(withYear ? { year: 'numeric' } : {}),
    timeZone: 'UTC',
  });
}

/** Shown wherever a change vs the previous period would be, when that period is not fully retained. */
export const NO_COMPARISON_LABEL = 'n/a — previous period incomplete';

/** "28 Aug – 26 Sep 2026 · compared with the previous 30 days". */
export function formatPeriod(summary: ExecutiveSummary): string {
  const days = Math.round((Date.parse(summary.range.to) - Date.parse(summary.range.from)) / 86_400_000);
  const dates = `${formatDay(summary.range.from, false)} – ${formatDay(summary.range.to, true)}`;
  if (!summary.comparison.available) return `${dates} · no comparison: previous period incomplete`;
  return `${dates} · compared with the previous ${days} ${days === 1 ? 'day' : 'days'}`;
}

/** "6 Jul" axis label for a trend point. */
export function formatWeek(iso: string): string {
  return formatDay(iso, false);
}

export function formatGeneratedAt(iso: string): string {
  return new Date(iso).toLocaleString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

/** Everything a KPI tile (on screen or on the slide) needs, derived once. */
export interface KpiView {
  key: 'active' | 'adoption' | 'habitual' | 'hours' | 'spend';
  label: string;
  value: string;
  delta: Delta | null;
  context: string;
}

export function buildKpis(s: ExecutiveSummary): KpiView[] {
  const { adoption } = s;
  const scope = adoption.denominatorSource === 'configured' ? 'engineers' : 'known developers';
  // No delta against a previous period that is not fully retained: its
  // placeholder zeros would otherwise read as "New" or as a large increase.
  const compare = s.comparison.available;
  return [
    {
      key: 'active',
      label: 'Active developers',
      value: formatCount(s.activeDevelopers.current),
      delta: compare ? percentChange(s.activeDevelopers.current, s.activeDevelopers.previous) : null,
      context: compare ? `vs ${formatCount(s.activeDevelopers.previous)} · counted once across tools` : 'counted once across tools',
    },
    {
      key: 'adoption',
      label: 'Adoption rate',
      value: formatPercent(adoption.current),
      delta: pointsChange(adoption.current, adoption.previous),
      context: `${formatCount(s.activeDevelopers.current)} of ${formatCount(adoption.denominator)} ${scope} · target ${adoption.targetPercent}%`,
    },
    {
      key: 'habitual',
      label: 'Habitual users',
      value: formatPercent(s.habitual.current),
      delta: pointsChange(s.habitual.current, s.habitual.previous),
      context: `${formatCount(s.habitual.count)} use AI on ${s.habitualDays}+ of ${s.workingDays} working days`,
    },
    {
      key: 'hours',
      label: 'AI-assisted hours',
      value: `${formatCount(s.activeHours.current)} h`,
      delta: compare ? percentChange(s.activeHours.current, s.activeHours.previous) : null,
      context: `${formatCount(s.activeHours.perDeveloper)} h per active developer`,
    },
    {
      key: 'spend',
      label: 'Indicative token spend',
      value: formatCompactMoney(s.spend.current, s.spend.currency),
      delta: compare ? percentChange(s.spend.current, s.spend.previous, false) : null,
      context: [`${formatMoney(s.spend.perDeveloper, s.spend.currency)} per developer on token-priced tools`, spendExclusionNote(s)].filter(Boolean).join(' · '),
    },
  ];
}
